/**
 * 通用批量更新工具类
 * 用于优化频繁的列表更新操作，将多次更新合并为一次批量更新
 */
export class BatchUpdate {
  constructor(options = {}) {
    // 配置项
    this.debounceDelay = options.debounceDelay || 100; // 防抖延迟，默认 100ms
    this.findItemCallback = options.findItemCallback; // 查找列表项的回调函数
    this.updateCallback = options.updateCallback; // 自定义更新回调
    this.enableChangeCheck = options.enableChangeCheck !== false; // 是否检查变化

    // 内部状态
    this.updateQueue = new Map(); // id -> updates 对象
    this.timer = null;
    this.vueInstance = options.vueInstance; // Vue 实例，用于 $set 和 $nextTick
  }

  /**
   * 添加更新到队列
   * @param {string|number} id - 列表项的唯一标识（如 user_id）
   * @param {Object} updates - 要更新的属性对象
   * @param {boolean} immediate - 是否立即更新（跳过防抖）
   */
  queue(id, updates, immediate = false) {
    if (!id || !updates) return;

    // 立即更新
    if (immediate) {
      this.applyUpdate(id, updates);
      return;
    }

    // 合并到队列
    const existing = this.updateQueue.get(id) || {};
    this.updateQueue.set(id, { ...existing, ...updates });

    // 触发防抖更新
    this.schedule();
  }

  /**
   * 安排批量更新（防抖）
   */
  schedule() {
    if (this.timer) {
      clearTimeout(this.timer);
    }

    this.timer = setTimeout(() => {
      if (this.vueInstance) {
        this.vueInstance.$nextTick(() => {
          this.flush();
        });
      } else {
        this.flush();
      }
    }, this.debounceDelay);
  }

  /**
   * 立即执行所有待更新
   */
  flush() {
    if (this.updateQueue.size === 0) return;

    const updates = new Map(this.updateQueue);
    this.updateQueue.clear();

    // 批量应用更新
    updates.forEach((updatesObj, id) => {
      this.applyUpdate(id, updatesObj);
    });
  }

  /**
   * 应用单个更新
   */
  applyUpdate(id, updates) {
    if (!this.findItemCallback) {
      console.warn("[BatchUpdate] findItemCallback is required");
      return;
    }

    // 查找列表项
    const item = this.findItemCallback(id);
    if (!item) return;

    // 如果有自定义更新回调，使用它
    if (this.updateCallback) {
      this.updateCallback(item, updates, id);
      return;
    }

    // 默认更新逻辑：使用 Vue.$set
    Object.keys(updates).forEach((key) => {
      const newValue = updates[key];

      // 检查是否有变化
      if (this.enableChangeCheck && item[key] === newValue) {
        return; // 值未变化，跳过更新
      }

      // 更新属性
      if (this.vueInstance) {
        this.vueInstance.$set(item, key, newValue);
      } else {
        item[key] = newValue;
      }
    });
  }

  /**
   * 清空更新队列
   */
  clear() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.updateQueue.clear();
  }

  /**
   * 获取待更新数量
   */
  getPendingCount() {
    return this.updateQueue.size;
  }
}

/**
 * 创建批量更新实例的工厂函数
 * @param {Object} options - 配置选项
 * @returns {BatchUpdate} 批量更新实例
 */
export function createBatchUpdate(options) {
  return new BatchUpdate(options);
}
